import React, { useState } from 'react';
import { Youtube, Download, Loader, Check, AlertCircle, List } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const YouTubeImporter = ({ authHeader, onImportComplete }) => {
  const [channelUrl, setChannelUrl] = useState('');
  const [importAll, setImportAll] = useState(true);
  const [playlistId, setPlaylistId] = useState('');
  const [maxVideos, setMaxVideos] = useState(50);
  const [playlists, setPlaylists] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingPlaylists, setLoadingPlaylists] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');

  const extractChannelId = (url) => {
    if (url.includes('youtube.com/channel/')) {
      return url.split('youtube.com/channel/')[1].split('/')[0].split('?')[0];
    }
    return null;
  };

  const fetchPlaylists = async () => {
    const channelId = extractChannelId(channelUrl);
    if (!channelId) {
      setError('Please enter a valid YouTube channel URL first');
      return;
    }

    setLoadingPlaylists(true);
    setError('');

    try {
      const response = await fetch(
        `${BACKEND_URL}/api/admin/youtube/playlists/${channelId}`,
        authHeader
      );
      const data = await response.json();
      
      if (response.ok) {
        setPlaylists(data.playlists || []);
      } else {
        setError(data.detail || 'Failed to fetch playlists');
      }
    } catch (err) {
      setError('Failed to fetch playlists');
    } finally {
      setLoadingPlaylists(false);
    }
  };

  const handleImport = async () => {
    if (!channelUrl) {
      setError('Please enter a YouTube channel URL');
      return;
    }

    setLoading(true);
    setError('');
    setResult(null);

    try {
      const params = new URLSearchParams({
        channel_url: channelUrl,
        import_all: importAll.toString(),
        max_videos: maxVideos.toString()
      });
      
      if (!importAll && playlistId) {
        params.append('playlist_id', playlistId);
      }

      const response = await fetch(
        `${BACKEND_URL}/api/admin/youtube/import-channel?${params}`,
        {
          method: 'POST',
          ...authHeader
        }
      );
      
      const data = await response.json();
      
      if (response.ok) {
        setResult(data);
        if (onImportComplete) {
          onImportComplete(data);
        }
      } else {
        setError(data.detail || 'Import failed');
      }
    } catch (err) {
      setError('Import failed: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-gray-900 rounded-xl border border-gray-800 p-6" data-testid="youtube-importer">
      <div className="flex items-center gap-3 mb-6">
        <div className="bg-red-600/20 p-3 rounded-lg">
          <Youtube className="text-red-500" size={24} />
        </div>
        <div>
          <h3 className="text-xl font-bold text-white">YouTube Channel Importer</h3>
          <p className="text-gray-400 text-sm">Import videos from your YouTube channel</p>
        </div>
      </div>

      {error && (
        <div className="bg-red-500/10 border border-red-500/30 text-red-400 px-4 py-3 rounded-lg mb-4 flex items-center gap-2">
          <AlertCircle size={18} />
          {error}
        </div>
      )}

      {result && (
        <div className="bg-green-500/10 border border-green-500/30 text-green-400 px-4 py-3 rounded-lg mb-4 flex items-center gap-2">
          <Check size={18} />
          Imported {result.videos_imported} videos ({result.videos_skipped} skipped)
        </div>
      )}

      <div className="space-y-4">
        {/* Channel URL */}
        <div>
          <label className="block text-gray-400 text-sm mb-2">YouTube Channel URL</label>
          <input
            type="url"
            placeholder="https://youtube.com/channel/UC... or https://youtube.com/@username"
            value={channelUrl}
            onChange={(e) => setChannelUrl(e.target.value)}
            className="w-full bg-gray-800 border border-gray-700 rounded-lg px-4 py-3 text-white placeholder-gray-500 focus:border-red-500 focus:outline-none"
            data-testid="channel-url-input"
          />
        </div>

        {/* Import Options */}
        <div className="flex items-center gap-4">
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="radio"
              checked={importAll}
              onChange={() => setImportAll(true)}
              className="text-red-600"
            />
            <span className="text-white">Import all videos</span>
          </label>
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="radio"
              checked={!importAll}
              onChange={() => { setImportAll(false); fetchPlaylists(); }}
              className="text-red-600"
            />
            <span className="text-white">Import from playlist</span>
          </label>
        </div>

        {/* Playlist Selection */}
        {!importAll && (
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="text-gray-400 text-sm">Select Playlist</label>
              <button
                onClick={fetchPlaylists}
                disabled={loadingPlaylists}
                className="text-red-500 text-sm hover:text-red-400"
              >
                {loadingPlaylists ? 'Loading...' : 'Refresh Playlists'}
              </button>
            </div>
            {playlists.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2 max-h-48 overflow-y-auto">
                {playlists.map((playlist) => (
                  <button
                    key={playlist.id}
                    onClick={() => setPlaylistId(playlist.id)}
                    className={`flex items-center gap-3 p-3 rounded-lg border transition-colors ${
                      playlistId === playlist.id
                        ? 'bg-red-600/20 border-red-500'
                        : 'bg-gray-800 border-gray-700 hover:border-gray-600'
                    }`}
                  >
                    {playlist.thumbnail && (
                      <img src={playlist.thumbnail} alt="" className="w-16 h-10 rounded object-cover" />
                    )}
                    <div className="text-left flex-1 min-w-0">
                      <p className="text-white text-sm font-medium truncate">{playlist.title}</p>
                      <p className="text-gray-500 text-xs">{playlist.video_count} videos</p>
                    </div>
                  </button>
                ))}
              </div>
            ) : (
              <div className="text-gray-500 text-sm text-center py-4">
                {loadingPlaylists ? 'Loading playlists...' : 'Enter channel URL and click "Refresh Playlists"'}
              </div>
            )}
          </div>
        )}

        {/* Max Videos */}
        <div>
          <label className="block text-gray-400 text-sm mb-2">Maximum Videos to Import</label>
          <input
            type="number"
            min="1"
            max="500"
            value={maxVideos}
            onChange={(e) => setMaxVideos(parseInt(e.target.value) || 50)}
            className="w-32 bg-gray-800 border border-gray-700 rounded-lg px-4 py-2 text-white focus:border-red-500 focus:outline-none"
          />
        </div>

        {/* Import Button */}
        <button
          onClick={handleImport}
          disabled={loading || !channelUrl}
          className="w-full bg-red-600 hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed text-white py-3 rounded-lg font-medium flex items-center justify-center gap-2"
          data-testid="import-btn"
        >
          {loading ? (
            <>
              <Loader size={20} className="animate-spin" />
              Importing...
            </>
          ) : (
            <>
              <Download size={20} />
              Import Videos
            </>
          )}
        </button>
      </div>
    </div>
  );
};

export default YouTubeImporter;
